
import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Client {
  id: string;
  client_name: string;
  last_contact_date: string;
}

interface ContentGenerationCardProps {
  clients: Client[];
  onGenerate: (clientId: string, messageType: "email" | "text" | "both") => Promise<void>;
}

export default function ContentGenerationCard({ clients, onGenerate }: ContentGenerationCardProps) {
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"email" | "text" | "both">("email");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!selectedClient) return;
    setIsGenerating(true);
    try {
      await onGenerate(selectedClient, messageType);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-500 via-blue-400 to-green-400 text-white">
        <CardTitle>Generate New Content</CardTitle>
        <CardDescription className="text-white/90">
          Select a client and message type to generate personalized content
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Client</label>
            <Select value={selectedClient || ''} onValueChange={setSelectedClient}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a client" />
              </SelectTrigger>
              <SelectContent>
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex justify-between items-center gap-4">
                      <span>{client.client_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(client.last_contact_date), 'PP')}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Message Type</label>
            <Select value={messageType} onValueChange={(value: "email" | "text" | "both") => setMessageType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Choose message type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            className="w-full"
            onClick={handleGenerate}
            disabled={!selectedClient || isGenerating}
          >
            {isGenerating ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                Generating...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                Generate Content
                <span className="h-4 w-4">→</span>
              </div>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
